import React, { createContext, useContext, useState } from 'react';

import { DateRange } from '@components/DateRangePicker/DateRangePickerModal';
import {
  MyTasksGroupBy,
  MyTasksOrderBy,
} from '@components/MyTasks/myTasksGrouping';
import { TaskPriority, User } from '@graphql/generated';

// ProjectTasksScreen's own assignee/priority/due-date filters (plus its
// group-by/order-by selection) — kept fully separate from useTasksFilter (My
// Tasks') so applying a filter on Project Tasks never leaks onto My Tasks, or
// vice versa. ProjectTasksScreen resets this back to its initial state every
// time it mounts (see resetProjectTaskFilters below), so anything applied on
// a previous visit never carries over either.

export type ProjectTaskUserFilter = {
  type: 'TasksFilterUser';
  name: string;
  value: User['id'];
};

export type ProjectTaskPriorityFilter = {
  type: 'PriorityFilterUser';
  name: string;
  value: TaskPriority;
};

export type ProjectTaskDueDateFilter = {
  type: 'DueDateFilter';
  name: string;
  value: string;
};

export type ProjectTaskDueDateStrFilter = {
  type: 'DueDateStrFilter';
  name: string;
  value: DateRange;
};

type ProjectTaskFilterContext = {
  userFilters: ProjectTaskUserFilter[];
  setUserFilters: React.Dispatch<React.SetStateAction<ProjectTaskUserFilter[]>>;
  priorityFilters: ProjectTaskPriorityFilter[];
  setPriorityFilters: React.Dispatch<
    React.SetStateAction<ProjectTaskPriorityFilter[]>
  >;
  dueDateFilters: ProjectTaskDueDateFilter[];
  setDueDateFilters: React.Dispatch<
    React.SetStateAction<ProjectTaskDueDateFilter[]>
  >;
  dueDateStrFilters: ProjectTaskDueDateStrFilter;
  setDueDateStrFilters: React.Dispatch<
    React.SetStateAction<ProjectTaskDueDateStrFilter>
  >;
  // Grouping/ordering for ProjectTasksScreen's own group list — separate
  // from useTasksFilter's so it never shares state with My Tasks either.
  groupByOption: MyTasksGroupBy;
  setGroupByOption: React.Dispatch<React.SetStateAction<MyTasksGroupBy>>;
  orderByOption: MyTasksOrderBy;
  setOrderByOption: React.Dispatch<React.SetStateAction<MyTasksOrderBy>>;
  resetProjectTaskFilters: () => void;
};

const projectTaskFilterContext = createContext<
  ProjectTaskFilterContext | undefined
>(undefined);

const getInitialDueDateStrFilters = (): ProjectTaskDueDateStrFilter => ({
  type: 'DueDateStrFilter',
  name: '',
  value: { start: undefined, end: undefined },
});

export const ProjectTaskFilterProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { Provider } = projectTaskFilterContext;

  const [userFilters, setUserFilters] = useState<ProjectTaskUserFilter[]>([]);
  const [priorityFilters, setPriorityFilters] = useState<
    ProjectTaskPriorityFilter[]
  >([]);
  const [dueDateFilters, setDueDateFilters] = useState<
    ProjectTaskDueDateFilter[]
  >([]);
  const [dueDateStrFilters, setDueDateStrFilters] =
    useState<ProjectTaskDueDateStrFilter>(getInitialDueDateStrFilters());
  // Defaults to Assignee, not Project — grouping by Project is meaningless
  // once a screen is already scoped to a single project (ProjectTasksScreen
  // hides the Project chip from its filter sheet for the same reason).
  const [groupByOption, setGroupByOption] = useState<MyTasksGroupBy>(
    MyTasksGroupBy.Assignee
  );
  const [orderByOption, setOrderByOption] = useState<MyTasksOrderBy>(
    MyTasksOrderBy.OverdueToLow
  );

  const resetProjectTaskFilters = () => {
    setUserFilters([]);
    setPriorityFilters([]);
    setDueDateFilters([]);
    setDueDateStrFilters(getInitialDueDateStrFilters());
    setGroupByOption(MyTasksGroupBy.Assignee);
    setOrderByOption(MyTasksOrderBy.OverdueToLow);
  };

  return (
    <Provider
      value={{
        userFilters,
        setUserFilters,
        priorityFilters,
        setPriorityFilters,
        dueDateFilters,
        setDueDateFilters,
        dueDateStrFilters,
        setDueDateStrFilters,
        groupByOption,
        setGroupByOption,
        orderByOption,
        setOrderByOption,
        resetProjectTaskFilters,
      }}>
      {children}
    </Provider>
  );
};

export const useProjectTaskFilter = (): ProjectTaskFilterContext => {
  const context = useContext(projectTaskFilterContext);
  if (context === undefined) {
    throw new Error(
      'useProjectTaskFilter must be used within a ProjectTaskFilterProvider'
    );
  }
  return context;
};
